import type { FlattenSimpleInterpolation } from 'styled-components';
import styled from 'styled-components';
import {
  AudiCopy1WithHoverStyle,
  generateMobileMenuMaxHeightCss,
  timeHeaderTransitionDelay,
  timeM,
  timeXL,
} from '../AudiHeader/AudiHeaderStyleUtils';
import type { HeaderStyledSubNavProps } from '../../interfaces/header-styled-components.interfaces';
import { createBreakpointStyles } from '../../services';
import { responsiveStyles } from '../../utils/responsiveStyles';
import type { CustomTheme } from '../../../@types/theme';
import { Text } from '@oneaudi/unified-web-components';

const headerSubNavStyles = (
  theme: CustomTheme,
  isSubNavOpen: boolean,
): Array<{ maxNavItems: number; style: unknown }> => [
  {
    maxNavItems: 0,
    style: null,
  },
  {
    maxNavItems: 4,
    style: {
      bottom: { l: 'initial' },
      height: {
        l: isSubNavOpen ? 'auto' : '0',
      },
      'max-height': {
        l: `${
          isSubNavOpen ? '660px' : '0'
        } /* 660px => teaser-image height * 2 - so it leaves space for about 8 cells of nav items in total */`,
      },
      opacity: { l: isSubNavOpen ? '1' : '0' },
      overflow: { l: 'hidden' },
      'overflow-y': { l: 'initial' },
      padding: {
        xl: '0 var(--one-header-side-spacing) 0 var(--one-header-flyout-left-spacing)',
      },
      top: { l: '72px' },
      transform: { l: 'none' },
      transition: {
        l: isSubNavOpen
          ? `max-height ${timeXL} ${theme.easing.easingDefault}, opacity ${timeM} ${theme.easing.easingDefault} ${timeHeaderTransitionDelay}`
          : `max-height ${theme.easing.easingDefault} ${timeXL}, opacity ${timeM} ${theme.easing.easingDefault}`,
      },
      'z-index': { l: '-1' },
    },
  },
  {
    maxNavItems: 5,
    style: {
      bottom: { xl: 'initial' },
      height: {
        xl: isSubNavOpen ? 'auto' : '0',
      },
      'max-height': {
        xl: `${
          isSubNavOpen ? '660px' : '0'
        } /* 660px => teaser-image height * 2 - so it leaves space for about 8 cells of nav items in total */`,
      },
      opacity: { xl: isSubNavOpen ? '1' : '0' },
      overflow: { xl: 'hidden' },
      'overflow-y': { xl: 'initial' },
      padding: {
        xl: '0 var(--one-header-side-spacing) 0 var(--one-header-flyout-left-spacing)',
      },
      top: { xl: '72px' },
      transform: { xl: 'none' },
      transition: {
        xl: isSubNavOpen
          ? `max-height ${timeXL} ${theme.easing.easingDefault}, opacity ${timeM} ${theme.easing.easingDefault} ${timeHeaderTransitionDelay}`
          : `max-height ${timeXL} ${theme.easing.easingDefault}, opacity ${timeM} ${theme.easing.easingDefault}`,
      },
      'z-index': { xl: '-1' },
    },
  },
  {
    maxNavItems: 8,
    style: {
      bottom: { xxl: 'initial' },
      height: {
        xxl: isSubNavOpen ? 'auto' : '0',
      },
      'max-height': {
        xxl: `${
          isSubNavOpen ? '660px' : '0'
        } /* 660px => teaser-image height * 2 - so it leaves space for about 8 cells of nav items in total */`,
      },
      opacity: { xxl: isSubNavOpen ? '1' : '0' },
      overflow: { xxl: 'hidden' },
      'overflow-y': { xxl: 'initial' },
      padding: {
        xxl: '0 var(--one-header-side-spacing) 0 var(--one-header-flyout-left-spacing)',
      },
      top: { xxl: '72px' },
      transform: { xxl: 'none' },
      transition: {
        xxl: isSubNavOpen
          ? `max-height ${timeXL} ${theme.easing.easingDefault}, opacity ${timeM} ${theme.easing.easingDefault} ${timeHeaderTransitionDelay}`
          : `max-height ${timeXL} ${theme.easing.easingDefault} ${timeHeaderTransitionDelay}, opacity ${timeM} ${theme.easing.easingDefault}`,
      },
      'z-index': { xxl: '-1' },
    },
  },
  {
    maxNavItems: -1,
    style: null,
  },
];

export const HeaderStyledSubNav = styled.div<HeaderStyledSubNavProps>`
  backface-visibility: hidden;
  background-color: ${({ theme }): string =>
    theme.type === 'legacy'
      ? 'var(--one-header-color-white)'
      : 'var(--one-header-color-canvas-default)'};
  bottom: 0;
  display: block;
  visibility: ${({ isSubNavOpen }): string => (isSubNavOpen ? 'visible' : 'hidden')};
  height: ${({ isSubNavOpen, isDealerSearch }): string =>
    isSubNavOpen ? generateMobileMenuMaxHeightCss(isDealerSearch) : '0'};
  left: 0;
  /* ${({ headerNavigationItemsAmount, theme, isSubNavOpen }): string =>
    responsiveStyles(
      createBreakpointStyles(headerNavigationItemsAmount, headerSubNavStyles(theme, isSubNavOpen)),
      theme.breakpoints,
    )} */
  overflow-y: ${({ isSubNavOpen }): string => (isSubNavOpen ? 'auto' : 'hidden')};
  padding: 0 var(--one-header-side-spacing);
  position: absolute;
  right: 0;
  top: 0;
  transform: ${({ isSubNavOpen }): string =>
    isSubNavOpen ? 'translateX(0)' : 'translateX(100%) scaleY(1)'};
  transition:
    transform ${({ theme }): string => theme.easing.easingDefault} ${timeXL},
    height ${({ theme }): string => theme.easing.easingDefault} ${timeXL};

  ${({ isNavigationOverflow, isSubNavOpen, theme: { breakpoints, easing } }) =>
    !isNavigationOverflow &&
    `@media screen and (min-width: ${breakpoints.l}px) {
          bottom: initial;
          height: ${isSubNavOpen ? 'auto' : '0'};
          max-height: ${isSubNavOpen ? '660px' : '0'};
          opacity: ${isSubNavOpen ? '1' : '0'};
          overflow: hidden;
          overflow-y: initial;
          padding: 0 var(--one-header-side-spacing) 0 var(--one-header-flyout-left-spacing);
          top: 72px;
          transform: none;
          transition: ${
            isSubNavOpen
              ? `max-height ${timeXL} ${easing.easingDefault}, opacity ${timeM} ${easing.easingDefault} ${timeHeaderTransitionDelay}`
              : `max-height ${timeXL} ${easing.easingDefault} ${timeHeaderTransitionDelay}, opacity ${timeM} ${easing.easingDefault}`
          };
          z-index: -1;
        }
      `}
`;

export const HeaderStyledSubNavButton = styled.button<{
  headerNavigationItemsAmount: number;
  isNavigationOverflow: boolean;
}>`
  align-items: center;
  background-color: ${({ theme }): string =>
    theme.type === 'legacy'
      ? 'var(--one-header-color-white)'
      : 'var(--one-header-color-canvas-default)'};
  border: none;
  border-bottom: 1px solid #ccc;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};

  color: ${({ theme }): string =>
    theme.type === 'legacy' ? 'var(--one-header-color-black)' : 'var(--one-header-color-link)'};
  cursor: pointer;
  display: flex;
  flex-flow: row nowrap;
  font-size: 1em;
  ${({ theme }): FlattenSimpleInterpolation => AudiCopy1WithHoverStyle(theme)}
  padding: var(--one-header-space-xl) 0 var(--one-header-space-m) 0;
  width: 100%;

  ${({ isNavigationOverflow, theme: { breakpoints } }) =>
    !isNavigationOverflow &&
    `@media screen and (min-width: ${breakpoints.l}px) {
        display: none;
      }
      `}

  &:hover,
  &:active,
  &:focus {
    color: ${({ theme }): string =>
      theme.type === 'legacy' ? 'var(--one-header-color-black)' : 'var(--one-header-color-hover)'};
  }

  & svg {
    height: 24px;
    margin-right: var(--one-header-space-s);
    transform: scaleX(-1);
    width: 24px;
  }
`;

export const HeaderStyledSubNavTeaserWrapper = styled.div<{
  headerNavigationItemsAmount: number;
  isNavigationOverflow: boolean;
}>`
  background-color: ${({ theme }): string =>
    theme.type === 'legacy'
      ? 'var(--one-header-color-white)'
      : '--one-header-color-canvas-default'};
  display: flex;
  justify-content: space-between;
  margin: var(--one-header-space-xl) 0 0 0;
  overflow: visible;

  ${({ isNavigationOverflow, theme }) =>
    !isNavigationOverflow &&
    `
      @media screen and (min-width: ${theme.breakpoints.l}px) {
        margin: var(--one-header-space-xxl) 0 0 0;
      }
    `};
`;

export const HeaderStyledSubNavListContainer = styled.nav<{
  headerNavigationItemsAmount: number;
  isNavigationOverflow: boolean;
  categories: number;
}>`
  margin: 0 0 var(--one-header-space-xxxl);
  ${({ isNavigationOverflow, theme }): string =>
    responsiveStyles(
      {
        'column-gap': {
          xl: 'var(--one-header-space-xxl)',
        },
        'margin-right': {
          m: 'var(--one-header-space-xl)',
        },
      },
      theme.breakpoints,
    )}
`;

export const NamedCategoryList = styled.li`
  margin-bottom: ${({ theme }): string => theme.spacing.xl};
  break-inside: avoid;
  @media (min-width: ${({ theme }): string => `${theme.breakpoints.xl}px`}) {
    ${({ theme }): string =>
      responsiveStyles(
        {
          'column-gap': {
            xl: 'var(--one-header-space-xxl)',
          },
          'margin-right': {
            m: 'var(--one-header-space-xl)',
          },
        },
        theme.breakpoints,
      )}
  }
`;

export const NamedStyledUnorderedList = styled.ul`
  padding: 0;
  list-style-type: none;
  ${({ theme }): string =>
    responsiveStyles(
      {
        width: {
          s: '320px',
          xs: '100%',
        },
      },
      theme.breakpoints,
    )}
`;

export const CategoryLabel = styled(Text)``;

export const HeaderStyledSubNavList = styled.ul<{
  headerNavigationItemsAmount: number;
  categories: number;
  isBasicCategory: boolean;
  isNavigationOverflow: boolean;
}>`
  list-style: none;
  margin: 0;
  display: flex;
  flex-direction: ${({ isBasicCategory }) => (isBasicCategory ? 'column' : 'row')};

  ${({ theme, categories }): string =>
    responsiveStyles(
      {
        width: {
          // xl: '320px',
          xs: '100%',
        },
        'flex-wrap': {
          xs: 'wrap',
        },
      },
      theme.breakpoints,
    )}
  opacity: 1;
  padding: 0;
  // safari only fix.
  // translateZ(0) --> Triggers Safari (and other browsers) to repaint with correct z-ordering and bounds.
  // will-change --> Helps the browser prepare the layer ahead of time and avoid flicker or repaint cost.
  transform: translateZ(0);
  will-change: transform;
`;
